/**
 * ManualEntryDialog
 *
 * Eigenständiger Dialog in der Zeiterfassungs-Übersicht: Zeit für einen ODER
 * mehrere Mitarbeiter an einem Tag nachtragen (auch für Tage ganz ohne Buchung).
 *
 * - Mehrfachauswahl: dieselbe Zeit wird für alle gewählten Mitarbeiter
 *   nachgetragen (z. B. gemeinsamer Dienstgang / Team-Einsatz ohne Stempelung).
 * - Mitarbeiter (mit `time_tracking:edit`) können optional „Sofort nachtragen"
 *   wählen — die Einträge werden direkt ohne Genehmigung angelegt.
 * - Ohne diese Berechtigung wird je Mitarbeiter ein Nachtrags-Antrag zur
 *   Genehmigung erstellt.
 * - Bei genau einem gewählten Mitarbeiter wird der geplante Einsatz (Dienstplan)
 *   als Vorschlag angeboten.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { timeTrackingAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarPlus } from 'lucide-react';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

const today = () => new Date().toISOString().slice(0, 10);

export default function ManualEntryDialog({ open, onOpenChange, employees = [], onSaved }) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('time_tracking', 'edit');

  const [employeeIds, setEmployeeIds] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [date, setDate] = useState(today());
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [reason, setReason] = useState('');
  const [applyImmediately, setApplyImmediately] = useState(canEdit);
  const [busy, setBusy] = useState(false);
  const [planned, setPlanned] = useState(null);
  const [conflicts, setConflicts] = useState([]);

  const dupKeys = useMemo(() => getDuplicateNameKeys(employees), [employees]);
  const singleId = employeeIds.length === 1 ? employeeIds[0] : null;
  const selEmp = singleId ? employees.find((e) => e.id === singleId) : null;

  // Geplanten Einsatz nur bei genau einem gewählten Mitarbeiter laden
  useEffect(() => {
    if (!open || !singleId || !date) { setPlanned(null); return; }
    let active = true;
    timeTrackingAPI.getPlannedShift(singleId, date)
      .then((res) => { if (active) { setPlanned(res.data?.found ? res.data : null); } })
      .catch(() => { if (active) { setPlanned(null); } });
    return () => { active = false; };
  }, [open, singleId, date]);

  // Doppel-Eintrag-Warnung: prüft für alle gewählten Mitarbeiter + Tag, ob
  // bereits eine Buchung oder genehmigte Abwesenheit existiert.
  useEffect(() => {
    if (!open || employeeIds.length === 0 || !date) { setConflicts([]); return undefined; }
    let active = true;
    const t = setTimeout(() => {
      timeTrackingAPI.getNachtragConflicts(date, employeeIds)
        .then((res) => { if (active) setConflicts(res.data?.conflicts || []); })
        .catch(() => { if (active) setConflicts([]); });
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [open, employeeIds, date]);

  const applyPlanned = () => {
    if (!planned) return;
    if (planned.start_time) setClockIn(planned.start_time);
    if (planned.end_time) setClockOut(planned.end_time);
    setBreakMinutes(planned.pause_minutes || 0);
  };

  // Auto-Vorbefüllung: geplanten Einsatz übernehmen, solange der Nutzer noch
  // keine Zeiten eingetippt hat. So muss man den Nachtrag nur noch bestätigen.
  const [plannedAutofilled, setPlannedAutofilled] = useState(false);
  useEffect(() => {
    if (planned && planned.start_time && planned.end_time && !clockIn && !clockOut) {
      setClockIn(planned.start_time);
      setClockOut(planned.end_time);
      setBreakMinutes(planned.pause_minutes || 0);
      setPlannedAutofilled(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planned]);

  const dailyTargetMin = selEmp ? Math.round(((selEmp?.weekly_hours ?? 40) / 5) * 60) : null;
  const hhmmToMin = (t) => { if (!t) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const minToHHMM = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
  const minToLabel = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} Std`;
  const suggestEnd = () => {
    const start = hhmmToMin(clockIn);
    if (start == null || dailyTargetMin == null) return;
    const end = Math.min(23 * 60 + 59, start + dailyTargetMin + Number(breakMinutes || 0));
    setClockOut(minToHHMM(end));
  };

  const toggleEmp = (id) => {
    setEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const deselectConflicting = () => {
    const remove = new Set(conflicts.map((c) => c.employee_id));
    setEmployeeIds((prev) => prev.filter((id) => !remove.has(id)));
  };

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => `${e.last_name || ''} ${e.first_name || ''} ${e.employee_number || ''}`.toLowerCase().includes(q));
  }, [employees, empSearch]);

  useEffect(() => {
    if (open) {
      setEmployeeIds([]);
      setEmpSearch('');
      setDate(today());
      setClockIn('');
      setClockOut('');
      setBreakMinutes(0);
      setReason('');
      setApplyImmediately(canEdit);
      setPlannedAutofilled(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (employeeIds.length === 0) { toast.error('Bitte mindestens einen Mitarbeiter wählen'); return; }
    if (!date) { toast.error('Bitte ein Datum wählen'); return; }
    if (!clockIn || !clockOut) { toast.error('Bitte Beginn und Ende angeben'); return; }
    if (reason.trim().length < 3) { toast.error('Bitte einen Grund angeben (mind. 3 Zeichen)'); return; }
    setBusy(true);
    const toIso = (t) => (t ? `${date}T${t}:00` : null);
    const basePayload = {
      reason: reason.trim(),
      requested_date: date,
      requested_clock_in: toIso(clockIn),
      requested_clock_out: toIso(clockOut),
      requested_break_minutes: breakMinutes ? Number(breakMinutes) : null,
      apply_immediately: canEdit && applyImmediately,
    };
    let ok = 0;
    const failed = [];
    for (const id of employeeIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await timeTrackingAPI.requestCorrection({ ...basePayload, employee_id: id });
        ok += 1;
      } catch (err) {
        const emp = employees.find((e) => e.id === id);
        const msg = err?.response?.data?.detail;
        failed.push(`${emp ? formatLastFirst(emp, dupKeys) : id}${typeof msg === 'string' ? `: ${msg}` : ''}`);
      }
    }
    setBusy(false);

    const direct = basePayload.apply_immediately;
    if (failed.length === 0) {
      toast.success(
        ok === 1
          ? (direct ? 'Zeit direkt nachgetragen' : 'Nachtrags-Antrag gesendet')
          : (direct ? `${ok} Mitarbeiter direkt nachgetragen` : `${ok} Nachtrags-Anträge gesendet`),
      );
      onSaved?.();
      onOpenChange(false);
    } else if (ok > 0) {
      toast.warning(`${ok} erfolgreich, ${failed.length} fehlgeschlagen: ${failed.join(' · ')}`);
      onSaved?.();
    } else {
      toast.error(`Nachtrag fehlgeschlagen: ${failed.join(' · ')}`);
    }
  };

  const multi = employeeIds.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manual-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5" />Zeit nachtragen</DialogTitle>
          <DialogDescription>
            Arbeitszeit für einen oder mehrere Mitarbeiter an einem Tag erfassen – auch wenn sich niemand eingestempelt hat.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>
              Mitarbeiter <span className="text-rose-500">*</span>
              {employeeIds.length > 0 && (
                <span className="ml-1 text-xs text-muted-foreground">({employeeIds.length} ausgewählt)</span>
              )}
            </Label>
            <Input
              placeholder="Mitarbeiter suchen…"
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              data-testid="me-employee-search"
              className="mb-2"
            />
            <div className="max-h-40 overflow-y-auto rounded-md border border-border/60 divide-y" data-testid="me-employee-list">
              {filteredEmployees.length === 0 ? (
                <p className="px-2 py-3 text-xs text-muted-foreground">Keine Treffer</p>
              ) : (
                filteredEmployees.map((e) => (
                  <label
                    key={e.id}
                    className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-secondary/40"
                    data-testid={`me-employee-option-${e.id}`}
                  >
                    <Checkbox
                      checked={employeeIds.includes(e.id)}
                      onCheckedChange={() => toggleEmp(e.id)}
                      data-testid={`me-employee-check-${e.id}`}
                    />
                    <span className="text-sm">{formatLastFirst(e, dupKeys)}</span>
                  </label>
                ))
              )}
            </div>
            {multi && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="me-multi-hint">
                Dieselbe Zeit wird für alle {employeeIds.length} Mitarbeiter nachgetragen.
              </p>
            )}
          </div>

          {conflicts.length > 0 && (
            <div className="col-span-2 rounded-md border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-2 text-xs text-amber-800 dark:text-amber-300" data-testid="me-conflict-warning">
              <p className="font-medium mb-1">Achtung – für diesen Tag existiert bereits etwas:</p>
              <ul className="space-y-0.5 list-disc pl-4">
                {conflicts.map((c) => {
                  const emp = employees.find((e) => e.id === c.employee_id);
                  const name = emp ? formatLastFirst(emp, dupKeys) : c.employee_id;
                  const parts = [];
                  if (c.has_booking) parts.push('bereits eine Buchung');
                  if (c.absence) parts.push(`Abwesenheit: ${c.absence.label}`);
                  return (
                    <li key={c.employee_id} data-testid={`me-conflict-${c.employee_id}`}>
                      <span className="font-semibold">{name}</span> — {parts.join(' · ')}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-1">Bitte prüfen, ob ein Nachtrag wirklich gewünscht ist (Doppel-Einträge vermeiden).</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-7 px-2 text-xs border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
                onClick={deselectConflicting}
                data-testid="me-deselect-conflicting"
              >
                Betroffene abwählen ({conflicts.length})
              </Button>
            </div>
          )}
          {planned && (
            <div className="col-span-2 flex items-center justify-between rounded-md border border-sky-300/60 bg-sky-50 dark:bg-sky-950/30 px-2.5 py-1.5 text-xs" data-testid="me-planned-shift">
              <span className="text-sky-800 dark:text-sky-300">
                {plannedAutofilled ? 'Aus Dienstplan übernommen: ' : 'Geplanter Einsatz: '}
                <span className="font-semibold">{planned.start_time}–{planned.end_time}</span>
                {planned.pause_minutes ? ` · Pause ${planned.pause_minutes} Min` : ''}
                {planned.massnahme_name ? ` · ${planned.massnahme_name}` : ''}
              </span>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={applyPlanned} data-testid="me-apply-planned">
                {plannedAutofilled ? 'Erneut übernehmen' : 'Übernehmen'}
              </Button>
            </div>
          )}
          <div className="col-span-2">
            <Label htmlFor="me-date">Datum <span className="text-rose-500">*</span></Label>
            <Input id="me-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="me-date" />
          </div>
          <div>
            <Label htmlFor="me-clock-in">Beginn <span className="text-rose-500">*</span></Label>
            <Input id="me-clock-in" type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)} data-testid="me-clock-in" />
          </div>
          <div>
            <Label htmlFor="me-clock-out">Ende <span className="text-rose-500">*</span></Label>
            <Input id="me-clock-out" type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)} data-testid="me-clock-out" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="me-break">Pause (Minuten)</Label>
            <Input id="me-break" type="number" min="0" max="240" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} data-testid="me-break" />
          </div>
          {dailyTargetMin != null && (
            <div className="col-span-2 flex items-center justify-between rounded-md border border-border/60 bg-secondary/30 px-2.5 py-1.5 text-xs" data-testid="me-target-hours">
              <span className="text-muted-foreground">Tages-Soll: <span className="font-medium text-foreground">{minToLabel(dailyTargetMin)}</span></span>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={!clockIn} onClick={suggestEnd} data-testid="me-suggest-end">
                Endzeit vorschlagen
              </Button>
            </div>
          )}
          <div className="col-span-2">
            <Label htmlFor="me-reason">Grund <span className="text-rose-500">*</span></Label>
            <Textarea id="me-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Stempeluhr vergessen / gemeinsamer Dienstgang ohne App" rows={2} data-testid="me-reason" />
          </div>
        </div>

        {canEdit && (
          <label className="flex items-start gap-2 rounded-md border border-border/60 bg-secondary/30 p-2.5 cursor-pointer" data-testid="me-apply-immediately-wrap">
            <Checkbox checked={applyImmediately} onCheckedChange={(v) => setApplyImmediately(!!v)} data-testid="me-apply-immediately" className="mt-0.5" />
            <span className="text-xs leading-snug">
              <span className="font-medium">Sofort nachtragen (ohne Genehmigung)</span>
              <span className="block text-muted-foreground">Andernfalls wird je Mitarbeiter ein Antrag zur Genehmigung erstellt.</span>
            </span>
          </label>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={busy} data-testid="me-submit">
            {busy
              ? 'Speichere...'
              : (canEdit && applyImmediately
                ? `Sofort nachtragen${multi ? ` (${employeeIds.length})` : ''}`
                : `Antrag senden${multi ? ` (${employeeIds.length})` : ''}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
